// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Color } from '../../../Graphics';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { InputParameterInteger } from '../InputParamaterClasses/InputParameterInteger';
import { InputParameterCombobox } from '../InputParamaterClasses/InputParameterCombobox';
import { GradientMode } from '../IndicatorConstants';
import { PriceType } from '@shared/utils/History/CashItemUtils';
var ICHLined;
(function (ICHLined) {
    ICHLined[ICHLined["TENKAN"] = 0] = "TENKAN";
    ICHLined[ICHLined["KIJUN"] = 1] = "KIJUN";
    ICHLined[ICHLined["SENKOU_SPANA"] = 2] = "SENKOU_SPANA";
    ICHLined[ICHLined["SENKOU_SPANB"] = 3] = "SENKOU_SPANB";
    ICHLined[ICHLined["CHINKOU_SPAN"] = 4] = "CHINKOU_SPAN";
})(ICHLined || (ICHLined = {}));
export class ICH extends IndicatorScriptBase {
    constructor() {
        super();
        this.ProjectName = 'Ichimoku';
        this.Comments = '';
        super.SetIndicatorLine('TENKAN', Color.Blue, 1, LineStyle.SimpleChart);
        super.SetIndicatorLine('KIJUN', Color.Lime, 1, LineStyle.SimpleChart);
        super.SetIndicatorLine('SENKOU_SPANA', Color.SpringGreen, 1, LineStyle.SimpleChart);
        super.SetIndicatorLine('SENKOU_SPANB', Color.Red, 1, LineStyle.SimpleChart);
        super.SetIndicatorLine('CHINKOU_SPAN', '#FF9900', 1, LineStyle.SimpleChart);
        this.TenkanPeriod = 9;
        super.InputParameter(new InputParameterInteger('TenkanPeriod', 'Tenkan Sen', this.TenkanPeriod, 1, 99999));
        this.KijunPeriod = 26;
        super.InputParameter(new InputParameterInteger('KijunPeriod', 'Kijun Sen', this.KijunPeriod, 1, 99999));
        this.SenkouSpanB = 52;
        super.InputParameter(new InputParameterInteger('SenkouSpanB', 'Senkou Span B', this.SenkouSpanB, 1, 99999));
        this.GradientMode = GradientMode.Simple;
        super.InputParameter(new InputParameterCombobox('GradientMode', 'Gradient method', this.GradientMode, [
            { Simple: GradientMode.Simple },
            { None: GradientMode.None },
            { Linear: GradientMode.Linear }
        ]));
        this.clouds.set('ICH_CLOUD', this.GradientMode, Color.SpringGreen, Color.Red);
        this.SeparateWindow = false;
    }
    Init() {
        this.IndicatorShortName(this.GetIndicatorShortName());
        this.UpdateIndexDrawBegin();
    }
    UpdateIndexDrawBegin() {
        this.SetLineShift(ICHLined.SENKOU_SPANA, this.KijunPeriod);
        this.SetLineShift(ICHLined.SENKOU_SPANB, this.KijunPeriod);
        this.clouds.get(0).timeShift = this.KijunPeriod;
    }
    GetIndicatorShortName() {
        return 'ICH(' + this.TenkanPeriod + ';' + this.KijunPeriod + ';' + this.SenkouSpanB + ')';
    }
    getCustomName() {
        return 'ICH (' + this.ProjectName + ')';
    }
    OnQuote() {
        super.OnQuote();
        const count = this.CurrentData.Count;
        if (count < this.TenkanPeriod || count < this.KijunPeriod || count < this.SenkouSpanB) {
            return;
        }
        this.SetValue(ICHLined.TENKAN, this.GetAverage(this.TenkanPeriod, 0));
        this.SetValue(ICHLined.KIJUN, this.GetAverage(this.KijunPeriod, 0));
        this.SetValue(ICHLined.CHINKOU_SPAN, this.KijunPeriod, this.CurrentData.getPriceByType(PriceType.Close));
        const senkouSpanA = (this.GetValue(ICHLined.TENKAN, 0) + this.GetValue(ICHLined.KIJUN, 0)) / 2;
        this.SetValue(ICHLined.SENKOU_SPANA, senkouSpanA);
        const senkouSpanB = this.GetAverage(this.SenkouSpanB, 0);
        this.SetValue(ICHLined.SENKOU_SPANB, senkouSpanB);
        this.clouds.get(0).set(senkouSpanA, senkouSpanB);
    }
    GetAverage(period, offset) {
        let high = this.CurrentData.getPriceByType(PriceType.High, offset);
        let low = this.CurrentData.getPriceByType(PriceType.Low, offset);
        let price = 0.0;
        for (let i = offset + 1; i < offset + period; i++) {
            price = this.CurrentData.getPriceByType(PriceType.High, i);
            if (high < price) {
                high = price;
            }
            price = this.CurrentData.getPriceByType(PriceType.Low, i);
            if (low > price) {
                low = price;
            }
        }
        return (high + low) / 2.0;
    }
}
